import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { freight } from "../../lib/freightApi.js";
import { useCarrier } from "./CarrierApp.jsx";
import { Shell, Back, Title, Card, Button, Note, Quiet, NotRunning, C, errText } from "./ui.jsx";

/**
 * Rules (the schematic's Settings · Rules board, with Brian's 10-30 cuts: "Add or modify" under the
 * button, "Modify user settings", "People in the loop · Edit", and nothing said twice).
 *
 * ⛔ TWO SECTIONS ARE MARKED "Not running yet", because they are: the escalation down People in the
 * loop and the alerts are not built (backend rules.mjs). Calling hours run once you set your own, and
 * the check calls decide what each load's page lists.
 *
 * An admin whose sign-in does not change rules sees them read-only, and still picks what they hear.
 */

const MODE = [{ v: "person", label: "Get a person now" }, { v: "summary", label: "Send a summary" }];
const HOW = [{ v: "text", label: "Text" }, { v: "email", label: "Email" }, { v: "both", label: "Text and email" }];
const WHEN = [{ v: "now", label: "Right away" }, { v: "thrice", label: "3 times a day" }, { v: "daily", label: "Once a day" }, { v: "weekly", label: "Once a week" }, { v: "off", label: "Off" }];
const STEP = { pickup: "at pickup", halfway: "halfway", before_delivery: "before delivery", after_delivery: "after delivery" };

/** Each rule's sentence, with its number in bold, as the card draws it. */
export const RULE_TEXT = {
  incident: () => <>If a driver reports an accident, an injury, a breakdown or the police. Alfred tells them to call 911 first if anyone is hurt.</>,
  asksForPerson: () => <>If a driver asks to talk to a person.</>,
  missedCheckCalls: (r) => <>If a driver misses <strong>{r.count}</strong> check calls in a row.</>,
  late: (r) => <>If a driver will be more than <strong>{r.minutes} minutes</strong> late for a pickup or delivery.</>,
  turnDowns: (r) => <>If a driver turns down a load, Alfred offers it to the next driver. After <strong>{r.count}</strong> turn-downs.</>,
  detention: (r) => <>If a driver has waited more than <strong>{r.hours} {r.hours === 1 ? "hour" : "hours"}</strong> at a shipper or receiver.</>,
};
const SELECT_LABEL = {
  asksForPerson: "What Alfred does when a driver asks for a person", missedCheckCalls: "What Alfred does when a driver misses check calls",
  late: "What Alfred does when a driver is running late", turnDowns: "What Alfred does after turn-downs",
  detention: "What Alfred does when a driver has waited too long",
};
const NUMBER = { missedCheckCalls: ["count", 1, 10, "check calls"], late: ["minutes", 5, 240, "minutes"], turnDowns: ["count", 1, 10, "turn-downs"], detention: ["hours", 1, 12, "hours"] };

export const ALERT_TEXT = {
  incident: ["An accident, injury, breakdown or police report", null],
  droppedLoad: ["A load is dropped", "A driver backs out, or the load is cancelled."],
  quitting: ["A driver says they're quitting", "Said straight out on a call."],
  lateForCustomer: ["A load is running late for a customer", "Past the window your rules set."],
  mightLeave: ["Drivers who might leave", "Signs from what drivers told Alfred."],
  applicantsReady: ["Applicants ready for a recruiter", null],
  doNotCall: ["Someone asked not to be called", null],
  savings: ["Savings and call totals", null],
};

const select = { minHeight: 44, padding: "0 10px", font: "inherit", fontSize: 15, border: `1px solid ${C.field}`, borderRadius: 8, background: C.card, color: C.ink };
const row = (last) => ({ padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px 16px", flexWrap: "wrap", borderBottom: last ? "none" : `1px solid ${C.soft}` });
const clock = (t) => { const [h, m] = String(t).split(":").map(Number); return `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, "0")}` : ""} ${h < 12 ? "AM" : "PM"}`; };
const listWords = (xs) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);

function NumberEdit({ label, value, lo, hi, onSave, onCancel, busy }) {
  const [v, setV] = useState(String(value));
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <input aria-label={label} type="number" min={lo} max={hi} value={v} onChange={(e) => setV(e.target.value)} style={{ ...select, width: 90 }} />
      <Button disabled={busy} onClick={() => onSave(Number(v))} style={{ minHeight: 44 }}>Save</Button>
      <Button kind="quiet" disabled={busy} onClick={onCancel}>Cancel</Button>
    </span>
  );
}

const modeWords = (v) => MODE.find((m) => m.v === v)?.label || v;

export function RulesView({ company, stage, me, rules, alerts, error, busy, msg, editing, setEditing, save, saveAlert, canChange = true }) {
  if (error) return <Shell company={company} stage={stage}><Back to="/carrier/settings">Settings</Back><Quiet>{error}</Quiet></Shell>;
  if (!rules) return <Shell company={company} stage={stage}><Back to="/carrier/settings">Settings</Back><Quiet>Loading…</Quiet></Shell>;
  const keys = Object.keys(RULE_TEXT);
  const hrs = rules.callingHours;
  return (
    <Shell company={company} stage={stage}>
      <Back to="/carrier/settings">Settings</Back>
      <Title>Rules</Title>
      <div style={{ display: "flex", gap: "12px 24px", flexWrap: "wrap", alignItems: "flex-start" }}>
        {canChange ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
            <Button onClick={() => setEditing(editing === "add" ? null : "add")} style={{ minHeight: 48, fontSize: 16, padding: "0 22px" }}>Add a rule</Button>
            <span style={{ fontSize: 14, color: C.muted }}>Add or modify</span>
          </div>
        ) : <Quiet>Your sign-in does not change rules. Ask the owner if that should change.</Quiet>}
        {me?.admin && <Link to="/carrier/settings/access" style={{ display: "inline-flex", alignItems: "center", minHeight: 48, fontSize: 15, fontWeight: 600, textDecoration: "none" }}>Modify user settings</Link>}
      </div>
      {canChange && editing === "add" && <AddRule busy={busy} onCancel={() => setEditing(null)} onAdd={(text, mode) => save({ custom: [...rules.custom, { text, mode }] }, "Rule added.")} />}
      <Note msg={msg} />

      <section aria-labelledby="person-title" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "8px 16px", flexWrap: "wrap" }}>
          <span style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <h2 id="person-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>When something needs a person</h2>
            <NotRunning />
          </span>
          <span style={{ fontSize: 15, color: C.muted }}>People in the loop · <Link to="/carrier/settings/people" aria-label="Edit People in the loop" style={{ fontWeight: 600, textDecoration: "none" }}>Edit</Link></span>
        </div>
        <Card style={{ display: "flex", flexDirection: "column" }}>
          {keys.map((k, i) => {
            const r = rules.person[k];
            const last = i === keys.length - 1 && rules.custom.length === 0;
            return (
              <div key={k} style={row(last)}>
                <p style={{ margin: 0, fontSize: 17, lineHeight: 1.5, flex: "1 1 420px", minWidth: 0 }}>{RULE_TEXT[k](r)}</p>
                {r.fixed ? (
                  <span style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>Get a person now</span>
                    <span style={{ fontSize: 15, fontWeight: 600, color: C.muted, whiteSpace: "nowrap" }}>Always on</span>
                  </span>
                ) : !canChange ? (
                  <span style={{ fontSize: 15, fontWeight: 600 }}>{modeWords(r.mode)}</span>
                ) : editing === k && NUMBER[k] ? (
                  <NumberEdit label={`How many ${NUMBER[k][3]}`} value={r[NUMBER[k][0]]} lo={NUMBER[k][1]} hi={NUMBER[k][2]} busy={busy}
                    onCancel={() => setEditing(null)} onSave={(n) => save({ person: { [k]: { [NUMBER[k][0]]: n } } }, "Saved.")} />
                ) : (
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <select aria-label={SELECT_LABEL[k]} value={r.mode} disabled={busy} onChange={(e) => save({ person: { [k]: { mode: e.target.value } } }, "Saved.")} style={select}>
                      {MODE.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
                    </select>
                    {NUMBER[k] && <Button kind="quiet" onClick={() => setEditing(k)} style={{ padding: "0 8px" }} label={`Edit the number of ${NUMBER[k][3]}`}>Edit</Button>}
                  </span>
                )}
              </div>
            );
          })}
          {rules.custom.map((c, i) => (
            <div key={c.id} style={row(i === rules.custom.length - 1)}>
              <p style={{ margin: 0, fontSize: 17, lineHeight: 1.5, flex: "1 1 420px", minWidth: 0 }}>{c.text}</p>
              {canChange ? (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <select aria-label={`What Alfred does: ${c.text}`} value={c.mode} disabled={busy}
                    onChange={(e) => save({ custom: rules.custom.map((x) => (x.id === c.id ? { ...x, mode: e.target.value } : x)) }, "Saved.")} style={select}>
                    {MODE.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
                  </select>
                  <Button kind="quiet" disabled={busy} onClick={() => save({ custom: rules.custom.filter((x) => x.id !== c.id) }, "Rule removed.")} label={`Remove the rule: ${c.text}`} style={{ padding: "0 8px" }}>Remove</Button>
                </span>
              ) : <span style={{ fontSize: 15, fontWeight: 600 }}>{modeWords(c.mode)}</span>}
            </div>
          ))}
        </Card>
      </section>

      {alerts && (
        <section aria-labelledby="hear-title" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <h2 id="hear-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>What you hear about</h2>
              <NotRunning />
            </span>
            <p style={{ margin: 0, fontSize: 15, color: C.muted }}>{`For you, ${me?.name || me?.email}. Each admin picks their own.`}</p>
          </div>
          <Card style={{ display: "flex", flexDirection: "column" }}>
            {Object.keys(ALERT_TEXT).map((k, i, all) => {
              const a = alerts[k];
              const [title, sub] = ALERT_TEXT[k];
              return (
                <div key={k} style={row(i === all.length - 1)}>
                  <span style={{ display: "flex", flexDirection: "column", gap: 2, flex: "1 1 300px", minWidth: 0 }}>
                    <span style={{ fontSize: 17 }}>{title}</span>
                    {sub && <span style={{ fontSize: 14, color: C.muted }}>{sub}</span>}
                  </span>
                  {a.fixed ? (
                    <span style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 15, fontWeight: 600 }}>Text, right away</span>
                      <span style={{ fontSize: 15, fontWeight: 600, color: C.muted }}>Always on</span>
                    </span>
                  ) : (
                    <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <select aria-label={`How you hear: ${title}`} value={a.how} disabled={busy} onChange={(e) => saveAlert({ [k]: { how: e.target.value } })} style={select}>
                        {HOW.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                      </select>
                      <select aria-label={`When you hear: ${title}`} value={a.when} disabled={busy} onChange={(e) => saveAlert({ [k]: { when: e.target.value } })} style={select}>
                        {WHEN.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                      </select>
                    </span>
                  )}
                </div>
              );
            })}
          </Card>
          {editing === "times" ? (
            <DigestTimes times={rules.digestTimes} busy={busy} onCancel={() => setEditing(null)} onSave={(t) => save({ digestTimes: t }, "Saved.")} />
          ) : (
            <p style={{ margin: 0, fontSize: 15, color: C.muted }}>
              {`"3 times a day" goes out at ${listWords(rules.digestTimes.thrice.map(clock))}; "Once a day" at ${clock(rules.digestTimes.daily)}. `}
              {canChange && <Button kind="quiet" onClick={() => setEditing("times")} style={{ padding: 0, minHeight: 32 }}>Change times</Button>}
            </p>
          )}
        </section>
      )}

      <section aria-labelledby="checks-title" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <h2 id="checks-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Check calls</h2>
        <Card style={{ display: "flex", flexDirection: "column" }}>
          <div style={row(false)}>
            {editing === "steps" ? (
              <StepsEdit steps={rules.checkCalls.steps} busy={busy} onCancel={() => setEditing(null)} onSave={(steps) => save({ checkCalls: { steps } }, "Saved.")} />
            ) : (
              <>
                <p style={{ margin: 0, fontSize: 17, lineHeight: 1.5, flex: "1 1 420px", minWidth: 0 }}>
                  {rules.checkCalls.steps.length ? <>Call each driver <strong>{listWords(rules.checkCalls.steps.map((s) => STEP[s]))}</strong>.</> : "No check calls."}
                </p>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <NotRunning>Not placed automatically yet</NotRunning>
                  {canChange && <Button kind="quiet" onClick={() => setEditing("steps")} label="Edit when Alfred makes check calls" style={{ padding: "0 8px" }}>Edit</Button>}
                </span>
              </>
            )}
          </div>
          <div style={row(true)}>
            {editing === "hours" ? (
              <HoursEdit hours={hrs} busy={busy} onCancel={() => setEditing(null)} onSave={(h) => save({ callingHours: h }, "Saved. Alfred calls drivers only inside these hours now.")} />
            ) : (
              <>
                <p style={{ margin: 0, fontSize: 17, lineHeight: 1.5, flex: "1 1 420px", minWidth: 0 }}>
                  Call drivers only <strong>{`between ${clock(hrs.start)} and ${clock(hrs.end)}`}</strong>, their time.
                </p>
                {canChange && <Button kind="quiet" onClick={() => setEditing("hours")} label="Edit calling hours" style={{ padding: "0 8px" }}>Edit</Button>}
              </>
            )}
          </div>
        </Card>
      </section>
    </Shell>
  );
}

function AddRule({ onAdd, onCancel, busy }) {
  const [text, setText] = useState("");
  const [mode, setMode] = useState("summary");
  return (
    <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 12 }}>
      <label htmlFor="new-rule" style={{ fontSize: 14, fontWeight: 600 }}>The rule</label>
      <input id="new-rule" value={text} maxLength={300} onChange={(e) => setText(e.target.value)} placeholder="If a driver says the reefer is not holding temperature."
        style={{ ...select, minHeight: 48, fontSize: 16, padding: "0 14px" }} />
      <select aria-label="What Alfred does" value={mode} onChange={(e) => setMode(e.target.value)} style={{ ...select, alignSelf: "flex-start" }}>
        {MODE.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
      </select>
      <div style={{ display: "flex", gap: 12 }}>
        <Button disabled={busy || !text.trim()} onClick={() => onAdd(text.trim(), mode)}>Add the rule</Button>
        <Button kind="secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </Card>
  );
}

function StepsEdit({ steps, onSave, onCancel, busy }) {
  const [on, setOn] = useState(new Set(steps));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
      <fieldset style={{ border: 0, margin: 0, padding: 0, display: "flex", gap: 16, flexWrap: "wrap" }}>
        <legend style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Call each driver</legend>
        {Object.entries(STEP).map(([k, label]) => (
          <label key={k} htmlFor={`step-${k}`} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 16 }}>
            <input id={`step-${k}`} type="checkbox" checked={on.has(k)} onChange={() => setOn((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; })} style={{ width: 20, height: 20 }} />
            {label}
          </label>
        ))}
      </fieldset>
      <div style={{ display: "flex", gap: 12 }}>
        <Button disabled={busy} onClick={() => onSave([...on])}>Save</Button>
        <Button kind="secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function HoursEdit({ hours, onSave, onCancel, busy }) {
  const [start, setStart] = useState(hours.start);
  const [end, setEnd] = useState(hours.end);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
      <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 16 }}>
        Between <input aria-label="Start" type="time" min="08:00" max="21:00" value={start} onChange={(e) => setStart(e.target.value)} style={select} />
        and <input aria-label="End" type="time" min="08:00" max="21:00" value={end} onChange={(e) => setEnd(e.target.value)} style={select} />
      </span>
      <span style={{ fontSize: 14, color: C.muted }}>Inside 8 AM to 9 PM: Alfred never calls outside those, where the driver is.</span>
      <div style={{ display: "flex", gap: 12 }}>
        <Button disabled={busy} onClick={() => onSave({ start, end })}>Save</Button>
        <Button kind="secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function DigestTimes({ times, onSave, onCancel, busy }) {
  const [thrice, setThrice] = useState(times.thrice);
  const [daily, setDaily] = useState(times.daily);
  return (
    <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 12 }}>
      <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 16 }}>
        3 times a day: {thrice.map((t, i) => <input key={i} aria-label={`Time ${i + 1}`} type="time" value={t} onChange={(e) => setThrice((x) => x.map((y, j) => (j === i ? e.target.value : y)))} style={select} />)}
      </span>
      <span style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 16 }}>
        Once a day: <input aria-label="Once a day" type="time" value={daily} onChange={(e) => setDaily(e.target.value)} style={select} />
      </span>
      <div style={{ display: "flex", gap: 12 }}>
        <Button disabled={busy} onClick={() => onSave({ thrice, daily })}>Save</Button>
        <Button kind="secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </Card>
  );
}

export default function Rules() {
  const { carrier, me } = useCarrier();
  const [rules, setRules] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [canChange, setCanChange] = useState(!!me.canChangeRules);
  const [error, setError] = useState(me.admin || me.canChangeRules ? null : "Ask an admin at your company: your sign-in does not change rules.");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [editing, setEditing] = useState(null);
  useEffect(() => {
    if (!me.admin && !me.canChangeRules) return undefined;
    let alive = true;
    freight.rules().then((r) => { if (alive) { setRules(r.rules); setAlerts(r.alerts); setCanChange(r.canChange !== false); } }).catch((e) => alive && setError(errText(e)));
    return () => { alive = false; };
  }, [me.admin, me.canChangeRules]);
  const save = async (patch, ok) => {
    setBusy(true); setMsg(null);
    try { setRules(await freight.saveRules(patch)); setMsg({ ok: true, text: ok }); setEditing(null); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  const saveAlert = async (patch) => {
    setBusy(true); setMsg(null);
    try { setAlerts(await freight.saveAlerts(patch)); setMsg({ ok: true, text: "Saved." }); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  return <RulesView company={carrier.name} stage={carrier.stage} me={me} rules={rules} alerts={alerts} error={error} busy={busy} msg={msg}
    editing={editing} setEditing={setEditing} save={save} saveAlert={saveAlert} canChange={canChange} />;
}
